import BuildIcon from "@mui/icons-material/Build";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ClearIcon from "@mui/icons-material/Clear";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import {
	alpha,
	Box,
	Button,
	Chip,
	CircularProgress,
	IconButton,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { Roll } from "../../models/Roll";
import { LocationSelector } from "../LocationSelector";
import { rollStatusCodes } from "../RollStatusSelector";
import { RollPassesEditor } from "./RollPassesEditor";

type Props = {
	label: string;
	roll: Roll | null;
	isLoading?: boolean;
	disabled?: boolean;
	error?: string;
	sectionId?: number | null;
	standId?: number | null;
	diameter?: number | null;
	diameterError?: string;
	onDiameterChange?: (diameter: number | null) => void;
	onClick: () => void;
	onMaintenanceClick: () => void;
	onSetReadyClick: () => void;
	onClear?: () => void;
};

export const RollCard = ({
	label,
	roll,
	isLoading = false,
	disabled = false,
	error,
	sectionId,
	standId,
	diameter,
	diameterError,
	onDiameterChange,
	onClick,
	onMaintenanceClick,
	onSetReadyClick,
	onClear,
}: Props) => {
	const isEdgerRoll = roll?.RollTypeCode === "E";
	const displayDiameter = diameter ?? roll?.Diameter;
	const status = roll
		? rollStatusCodes.find((s) => s.Code === roll.RollStatusCode)
		: null;

	return (
		<Box
			sx={{
				position: "relative",
				borderRadius: 2,
				background: disabled
					? (theme) => theme.palette.grey[50]
					: "linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)",
				boxShadow: error
					? (theme) => `0 0 0 1.5px ${theme.palette.error.main}`
					: "0 2px 12px rgba(0, 0, 0, 0.08)",
				overflow: "hidden",
				transition: "all 0.2s ease",
			}}
		>
			<Box
				sx={{
					height: 3,
					background: (theme) =>
						error ? theme.palette.error.main : theme.palette.primary.main,
				}}
			/>

			<Box sx={{ p: 1.5 }}>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						mb: 0.5,
					}}
				>
					<Typography
						sx={{
							color: error ? "error.main" : "text.secondary",
							fontWeight: 600,
							fontSize: "0.65rem",
							lineHeight: 1,
						}}
					>
						{label}
					</Typography>
					{roll && onClear && (
						<IconButton
							size="small"
							onClick={onClear}
							sx={{
								p: 0.25,
								"&:hover": {
									bgcolor: (theme) => alpha(theme.palette.error.main, 0.1),
									color: "error.main",
								},
							}}
						>
							<ClearIcon sx={{ fontSize: 14 }} />
						</IconButton>
					)}
				</Box>

				{isLoading ? (
					<Box
						sx={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							minHeight: 60,
						}}
					>
						<CircularProgress size={24} thickness={4} />
					</Box>
				) : roll ? (
					<>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
							<Typography
								variant="subtitle1"
								sx={{
									flex: 2,
									fontWeight: 700,
									color: "text.primary",
									fontSize: "1rem",
									lineHeight: 1.2,
									cursor: "pointer",
									"&:hover": {
										color: "primary.main",
									},
								}}
								data-test-id="roll-name"
								onClick={onClick}
							>
								{roll.PartName}
							</Typography>
							{status && (
								<Chip
									label={status.Name}
									size="small"
									sx={{
										flex: 1,
										height: 20,
										fontSize: "0.65rem",
										fontWeight: 600,
										bgcolor: status.Color,
										color: status.TextColor,
									}}
								/>
							)}
						</Box>

						<Stack
							spacing={1}
							sx={{ mt: 1 }}
							onClick={(e) => e.stopPropagation()}
						>
							<LocationSelector
								label="Stand"
								value={standId!}
								onlyStands
								disabled
								helperText={
									standId != roll.StandId && roll.StandId
										? `Previous stand: ${roll.StandName}`
										: undefined
								}
							/>

							<TextField
								label="Diameter"
								type="number"
								size="small"
								fullWidth
								value={displayDiameter ?? ""}
								onChange={(e) => {
									const val = e.target.value;
									onDiameterChange?.(val ? parseFloat(val) : null);
								}}
								inputProps={{ step: "0.001" }}
								helperText={diameterError}
								error={!!diameterError}
								slotProps={{
									input: {
										sx: {
											"&.Mui-error .MuiOutlinedInput-notchedOutline": {
												borderColor: "warning.main",
											},
										},
									},
									inputLabel: {
										sx: {
											"&.Mui-error": { color: "warning.main" },
										},
									},
									formHelperText: {
										sx: {
											"&.Mui-error": { color: "warning.main" },
										},
									},
								}}
							/>

							{isEdgerRoll && (
								<Box
									sx={{
										p: 1,
										bgcolor: (theme) => alpha(theme.palette.grey[500], 0.06),
										borderRadius: 1,
									}}
								>
									<RollPassesEditor
										partId={roll.PartId}
										passes={roll.Passes}
										sectionId={sectionId}
										disabled={disabled}
									/>
								</Box>
							)}

							<Stack direction="row" spacing={0.5}>
								<Button
									variant="contained"
									size="small"
									startIcon={<BuildIcon sx={{ fontSize: 14 }} />}
									onClick={onMaintenanceClick}
									sx={{
										flex: 1,
										borderRadius: 1.5,
										textTransform: "none",
										fontWeight: 600,
										fontSize: "0.75rem",
										py: 0.5,
										boxShadow: "none",
										"&:hover": {
											boxShadow: "0 2px 8px rgba(59, 130, 246, 0.4)",
										},
									}}
									data-test-id="maintenance"
								>
									Maintenance
								</Button>
								<Button
									variant="outlined"
									size="small"
									startIcon={<CheckCircleOutlineIcon sx={{ fontSize: 14 }} />}
									onClick={onSetReadyClick}
									sx={{
										flex: 1,
										borderRadius: 1.5,
										textTransform: "none",
										fontWeight: 600,
										fontSize: "0.75rem",
										py: 0.5,
									}}
									data-test-id="set-ready"
								>
									Set Ready
								</Button>
							</Stack>
						</Stack>
					</>
				) : (
					<Box
						sx={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							minHeight: 60,
							gap: 1,
							py: 1,
							cursor: "pointer",
							"&:hover": {
								bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
							},
						}}
						onClick={onClick}
					>
						<TouchAppIcon
							sx={{
								fontSize: 18,
								color: (theme) => alpha(theme.palette.primary.main, 0.6),
							}}
						/>
						<Typography
							variant="caption"
							sx={{ color: "text.secondary", fontWeight: 500 }}
						>
							Click to select a roll
						</Typography>
					</Box>
				)}
			</Box>

			{error && (
				<Box sx={{ px: 1.5, pb: 1 }}>
					<Typography
						variant="caption"
						sx={{ color: "error.main", fontWeight: 500, fontSize: "0.7rem" }}
					>
						{error}
					</Typography>
				</Box>
			)}
		</Box>
	);
};
